import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, Shield, MessageSquare, PhoneCall, Users, Sticker,
  CreditCard, Settings as SettingsIcon, LogOut, Menu, X, ArrowLeft
} from "lucide-react";
import BrandMark from "../brand/BrandMark";
import { useAuth } from "../../context/AuthContext";
import { useInbox } from "../../context/InboxContext";
import { useI18n } from "../../i18n";
import { useAppBack } from "../../hooks/useAppBack";
import { isSectionActive } from "../../lib/navSections";
import { toast } from "sonner";

/**
 * RC-WEB-4 §2/§26/§34 — canonical authenticated shell.
 * RC-WEB-5 GATE 13/16 — localized labels + a Back control that returns to the
 * real previous in-app route (feature fallback only on direct entry).
 */

const NAV = [
  { to: "/dashboard", key: "overview", mobileKey: "overview_mobile", icon: LayoutDashboard, testid: "nav-overview", mobile: true },
  { to: "/dashboard/vehicles", key: "protections", icon: Shield, testid: "nav-protections", mobile: true },
  { to: "/mensajes", key: "messages", icon: MessageSquare, testid: "nav-messages", mobile: true, badge: true },
  { to: "/llamadas", key: "calls", icon: PhoneCall, testid: "nav-calls", mobile: true },
  { to: "/contactos-de-confianza", key: "trusted", icon: Users, testid: "nav-trusted" },
  { to: "/sticker-studio", key: "sticker", icon: Sticker, testid: "nav-sticker-studio" },
  { to: "/renovar", key: "membership", icon: CreditCard, testid: "nav-membership" },
  { to: "/settings", key: "settings", icon: SettingsIcon, testid: "nav-settings", mobile: true },
];

// RC-WEB-6 PHASE 10 — the active section is derived from the normalized route
// (nested routes included), never from stale manually maintained state.
const isActive = isSectionActive;

export const AppShell = ({ children, title, subtitle, actions, maxWidth = "max-w-6xl", backTo }) => {
  const { logout } = useAuth();
  const { unread } = useInbox();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = useAppBack(backTo || "/dashboard");
  const [open, setOpen] = useState(false);
  const [tagline, setTagline] = useState("");

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    let alive = true;
    axios.get("/api/shared/company")
      .then((r) => { if (alive) setTagline((r.data?.product_tagline || "").trim()); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success(t("app.nav.logged_out"));
  };

  const navLink = (item, variant = "sidebar") => {
    const Icon = item.icon;
    const active = isActive(location.pathname, item.to);
    const label = t(`app.nav.${item.key}`);
    if (variant === "bottom") {
      return (
        <Link
          key={item.to}
          to={item.to}
          data-testid={`${item.testid}-mobile`}
          className={`relative flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium transition-colors ${
            active ? "text-[#1B84FF]" : "text-slate-600"
          }`}
        >
          <Icon className="h-5 w-5" />
          {item.mobileKey ? t(`app.nav.${item.mobileKey}`) : label}
          {item.badge && unread > 0 && (
            <span className="absolute right-1/4 top-1 h-4 min-w-[16px] rounded-full bg-red-600 px-1 text-center text-[10px] leading-4 text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Link>
      );
    }
    return (
      <Link
        key={item.to}
        to={item.to}
        data-testid={item.testid}
        className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
          active
            ? "bg-[#1B84FF] text-white shadow-sm shadow-[#1B84FF]/30"
            : "text-slate-200 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">{label}</span>
        {item.badge && unread > 0 && (
          <span
            className="ml-auto min-w-[20px] rounded-full bg-red-600 px-1.5 text-center text-[11px] font-bold text-white"
            data-testid="nav-messages-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Link>
    );
  };

  const sidebar = (
    <div className="flex h-full flex-col gap-6 bg-[#0B2545] px-4 py-6">
      <Link to="/" className="px-2" data-testid="shell-logo-link">
        <BrandMark size="sm" onDark asLink={false} />
      </Link>
      <nav className="flex flex-1 flex-col gap-1" aria-label={t("app.nav.main")}>
        {NAV.map((item) => navLink(item))}
      </nav>
      <button
        onClick={handleLogout}
        data-testid="shell-logout-btn"
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:bg-red-500/20 hover:text-white"
      >
        <LogOut className="h-4 w-4" /> {t("app.nav.logout")}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F8FAFC]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block" data-testid="shell-sidebar">
        {sidebar}
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="shell-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[80%]">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-3 top-4 z-10 rounded-full bg-white/10 p-2 text-white"
              data-testid="shell-drawer-close"
              aria-label={t("app.nav.close_menu")}
            >
              <X className="h-4 w-4" />
            </button>
            {sidebar}
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
          <div className={`${maxWidth} mx-auto flex items-center gap-3 px-4 py-3 sm:px-6`}>
            <button
              onClick={() => setOpen(true)}
              className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 lg:hidden"
              data-testid="shell-menu-btn"
              aria-label={t("app.nav.open_menu")}
            >
              <Menu className="h-5 w-5" />
            </button>
            {backTo && (
              <button
                onClick={goBack}
                className="hidden rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100 lg:inline-flex"
                data-testid="shell-back-btn"
                aria-label={t("app.back")}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              {title && (
                <h1 className="truncate text-xl font-bold text-[#0B2545] sm:text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {title}
                </h1>
              )}
              {subtitle && <p className="truncate text-sm text-slate-600">{subtitle}</p>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {actions}
              <Link
                to="/mensajes"
                className="relative rounded-lg p-2 text-slate-700 transition-colors hover:bg-slate-100"
                data-testid="header-messages-link"
                aria-label={t("app.nav.messages")}
              >
                <MessageSquare className="h-5 w-5" />
                {unread > 0 && (
                  <span
                    className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-[18px] text-white"
                    data-testid="header-unread-badge"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </header>

        <main className={`${maxWidth} mx-auto px-4 pb-28 pt-6 sm:px-6 lg:pb-12`}>
          {backTo && (
            <button
              onClick={goBack}
              className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#0B2545] lg:hidden"
              data-testid="shell-back-btn-mobile"
            >
              <ArrowLeft className="h-4 w-4" /> {t("app.back")}
            </button>
          )}
          {children}
        </main>

        <footer className="border-t border-slate-200 px-4 py-6 text-center lg:pb-6">
          <p className="text-sm text-slate-600" data-testid="shell-footer-tagline">
            © 2026 AvisoCar. {tagline || t("app.nav.tagline")}
          </p>
        </footer>
      </div>

      <nav
        className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
        data-testid="shell-bottom-nav"
        aria-label={t("app.nav.bottom")}
      >
        {NAV.filter((i) => i.mobile).map((item) => navLink(item, "bottom"))}
      </nav>
    </div>
  );
};

export default AppShell;
